/**
 * SuperAdmin "Compare Baskets" page.
 *
 * Side-by-side structural diff of one basket from account A against one from
 * account B — basket fields, strategies (matched by name), and legs (matched
 * by leg_index). Reads both accounts via the SuperAdmin acting-as path and
 * diffs the canonical export bundles server-side (POST /admin/super/baskets/
 * compare).
 *
 * Guarded by the SuperAdmins Cognito group.
 */
import React, { useCallback, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CheckCircle2, GitCompareArrows, Home, RefreshCw, Search } from 'lucide-react';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { useToast } from '../components/common/ToastContainer';
import { useAuth } from '../context/AuthContext';
import basketCompareService, {
  CompareResult,
  FieldDiff,
  UserSearchResult,
} from '../services/basketCompareService';
import type { Basket } from '../types';

type SideKey = 'a' | 'b';

interface SideState {
  query: string;                  // email/client-id search text
  results: UserSearchResult[];
  searching: boolean;
  selected: UserSearchResult | null;
  baskets: Basket[];
  basketId: string;
  loadingBaskets: boolean;
}

const EMPTY_SIDE: SideState = {
  query: '',
  results: [],
  searching: false,
  selected: null,
  baskets: [],
  basketId: '',
  loadingBaskets: false,
};

/** Render any bundle value (primitive or nested composite) for the diff table. */
function renderValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

const FieldDiffTable: React.FC<{ diffs: FieldDiff[] }> = ({ diffs }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-gray-50 dark:bg-gray-700 border-y border-gray-200 dark:border-gray-700">
          <th className="text-left px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Field</th>
          <th className="text-left px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Account A</th>
          <th className="text-left px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Account B</th>
        </tr>
      </thead>
      <tbody>
        {diffs.map((d) => (
          <tr key={d.field} className="border-b border-gray-100 dark:border-gray-700/50">
            <td className="px-4 py-2 font-medium text-gray-700 dark:text-gray-200">{d.field}</td>
            <td className="px-4 py-2 font-mono text-xs text-amber-700 dark:text-amber-300 break-all">{renderValue(d.a)}</td>
            <td className="px-4 py-2 font-mono text-xs text-blue-700 dark:text-blue-300 break-all">{renderValue(d.b)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Chips: React.FC<{ items: Array<string | number>; tone: 'amber' | 'blue' }> = ({ items, tone }) => {
  const cls = tone === 'amber'
    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200'
    : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200';
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it, i) => (
        <span key={`${it}-${i}`} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
          {String(it)}
        </span>
      ))}
    </div>
  );
};

const SummaryCard: React.FC<{ label: string; value: React.ReactNode; color: string }> = ({ label, value, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</div>
    <div className={`text-xl font-bold mt-1 tabular-nums ${color}`}>{value}</div>
  </div>
);

const DiffView: React.FC<{ result: CompareResult }> = ({ result }) => {
  const { diff } = result;
  const s = diff.summary;
  const strategiesWithDiffs = diff.strategies.matched.filter(
    (m) => m.fields.length || m.legs.only_in_a.length || m.legs.only_in_b.length || m.legs.matched.length,
  );

  if (diff.identical) {
    return (
      <Card>
        <CardContent>
          <div className="flex items-center justify-center gap-2 py-12 text-green-700 dark:text-green-300">
            <CheckCircle2 className="h-6 w-6" />
            <span className="font-medium">The two baskets are identical.</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Basket fields" value={s.basket_field_diffs} color="text-amber-600 dark:text-amber-400" />
        <SummaryCard label="Only in A" value={s.strategies_only_in_a} color="text-amber-600 dark:text-amber-400" />
        <SummaryCard label="Only in B" value={s.strategies_only_in_b} color="text-blue-600 dark:text-blue-400" />
        <SummaryCard label="Differing strategies" value={s.strategies_with_diffs} color="text-gray-900 dark:text-white" />
      </div>

      {diff.basket.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Basket fields ({diff.basket.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <FieldDiffTable diffs={diff.basket} />
          </CardContent>
        </Card>
      )}

      {(diff.strategies.only_in_a.length > 0 || diff.strategies.only_in_b.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Strategy presence</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Only in Account A</div>
              {diff.strategies.only_in_a.length
                ? <Chips items={diff.strategies.only_in_a} tone="amber" />
                : <span className="text-sm text-gray-500 dark:text-gray-400">—</span>}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Only in Account B</div>
              {diff.strategies.only_in_b.length
                ? <Chips items={diff.strategies.only_in_b} tone="blue" />
                : <span className="text-sm text-gray-500 dark:text-gray-400">—</span>}
            </div>
          </CardContent>
        </Card>
      )}

      {strategiesWithDiffs.map((m, idx) => (
        <Card key={`${m.strategy_name}-${idx}`}>
          <CardHeader>
            <CardTitle>Strategy: {m.strategy_name}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {m.fields.length > 0 && <FieldDiffTable diffs={m.fields} />}
            {(m.legs.only_in_a.length > 0 || m.legs.only_in_b.length > 0) && (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                {m.legs.only_in_a.length > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-700 dark:text-gray-200">Legs only in A:</span>
                    <Chips items={m.legs.only_in_a} tone="amber" />
                  </div>
                )}
                {m.legs.only_in_b.length > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-700 dark:text-gray-200">Legs only in B:</span>
                    <Chips items={m.legs.only_in_b} tone="blue" />
                  </div>
                )}
              </div>
            )}
            {m.legs.matched.map((leg) => (
              <div key={`leg-${leg.leg_index}`} className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Leg {String(leg.leg_index)}</div>
                <FieldDiffTable diffs={leg.fields} />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const AccountSelector: React.FC<{
  label: string;
  side: SideState;
  onQueryChange: (v: string) => void;
  onSearch: () => void;
  onSelectUser: (u: UserSearchResult) => void;
  onClear: () => void;
  onBasketChange: (v: string) => void;
}> = ({ label, side, onQueryChange, onSearch, onSelectUser, onClear, onBasketChange }) => (
  <div className="space-y-3">
    <div className="text-sm font-semibold text-gray-900 dark:text-white">{label}</div>

    {!side.selected ? (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Search user by email</label>
        <div className="flex gap-2">
          <Input
            value={side.query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSearch(); }}
            placeholder="email or client id…"
          />
          <Button variant="secondary" size="sm" onClick={onSearch} disabled={side.query.trim().length < 2 || side.searching}>
            {side.searching ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
        {side.results.length > 0 && (
          <ul className="mt-2 border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-100 dark:divide-gray-700 max-h-56 overflow-auto">
            {side.results.map((u) => (
              <li key={u.user_id}>
                <button
                  type="button"
                  onClick={() => onSelectUser(u)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <div className="text-sm text-gray-900 dark:text-white truncate">{u.email || '—'}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {u.full_name || u.user_id}
                    {u.zebu_client_id ? ` · ${u.zebu_client_id}` : ''}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    ) : (
      <div className="flex items-center justify-between gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {side.selected.email || side.selected.user_id}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {side.selected.full_name || side.selected.user_id}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClear}>Change</Button>
      </div>
    )}

    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Basket</label>
      <select
        value={side.basketId}
        onChange={(e) => onBasketChange(e.target.value)}
        disabled={!side.selected || side.loadingBaskets || side.baskets.length === 0}
        className="w-full rounded-md bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
      >
        <option value="">
          {side.loadingBaskets
            ? 'Loading baskets…'
            : !side.selected
              ? 'Select a user first'
              : side.baskets.length ? 'Select a basket…' : 'No baskets'}
        </option>
        {side.baskets.map((b) => (
          <option key={b.basket_id} value={b.basket_id}>
            {b.basket_name}
          </option>
        ))}
      </select>
    </div>
  </div>
);

const SuperAdminBasketComparePage: React.FC = () => {
  const { isSuperAdmin } = useAuth();
  const { showError } = useToast();
  const [sideA, setSideA] = useState<SideState>(EMPTY_SIDE);
  const [sideB, setSideB] = useState<SideState>(EMPTY_SIDE);
  const [result, setResult] = useState<CompareResult | null>(null);
  const [comparing, setComparing] = useState(false);

  const setterFor = (key: SideKey) => (key === 'a' ? setSideA : setSideB);

  const searchUsers = useCallback(async (key: SideKey) => {
    const setter = setterFor(key);
    const q = (key === 'a' ? sideA : sideB).query.trim();
    if (q.length < 2) return;
    setter((p) => ({ ...p, searching: true }));
    try {
      const results = await basketCompareService.searchUsers(q);
      setter((p) => ({ ...p, results, searching: false }));
      if (results.length === 0) showError('No users match that email');
    } catch (err: any) {
      setter((p) => ({ ...p, searching: false }));
      showError(err.message || 'User search failed');
    }
  }, [sideA, sideB, showError]);

  const selectUser = useCallback(async (key: SideKey, user: UserSearchResult) => {
    const setter = setterFor(key);
    setter((p) => ({ ...p, selected: user, results: [], baskets: [], basketId: '', loadingBaskets: true }));
    try {
      const baskets = await basketCompareService.listBasketsForUser(user.user_id);
      setter((p) => ({ ...p, baskets, loadingBaskets: false }));
      if (baskets.length === 0) showError('No baskets found for that user');
    } catch (err: any) {
      setter((p) => ({ ...p, loadingBaskets: false }));
      showError(err.message || 'Failed to load baskets');
    }
  }, [showError]);

  const clearSelection = useCallback((key: SideKey) => {
    setterFor(key)((p) => ({ ...p, selected: null, results: [], baskets: [], basketId: '' }));
  }, []);

  const runCompare = useCallback(async () => {
    if (!sideA.selected || !sideB.selected) return;
    setComparing(true);
    setResult(null);
    try {
      const res = await basketCompareService.compare({
        user_id_a: sideA.selected.user_id,
        basket_id_a: sideA.basketId,
        user_id_b: sideB.selected.user_id,
        basket_id_b: sideB.basketId,
      });
      setResult(res);
    } catch (err: any) {
      showError(err.message || 'Compare failed');
    } finally {
      setComparing(false);
    }
  }, [sideA, sideB, showError]);

  if (!isSuperAdmin) return <Navigate to="/" replace />;

  const canCompare = !!(sideA.selected && sideA.basketId && sideB.selected && sideB.basketId) && !comparing;

  return (
    <StandardLayout>
      <PageHeader
        title="Compare Baskets"
        pageType="management"
        icon={<GitCompareArrows className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          { label: 'Platform Owner' },
        ]}
      />

      <Card>
        <CardHeader>
          <CardTitle>Select two baskets</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AccountSelector
              label="Account A"
              side={sideA}
              onQueryChange={(v) => setSideA((p) => ({ ...p, query: v }))}
              onSearch={() => searchUsers('a')}
              onSelectUser={(u) => selectUser('a', u)}
              onClear={() => clearSelection('a')}
              onBasketChange={(v) => setSideA((p) => ({ ...p, basketId: v }))}
            />
            <AccountSelector
              label="Account B"
              side={sideB}
              onQueryChange={(v) => setSideB((p) => ({ ...p, query: v }))}
              onSearch={() => searchUsers('b')}
              onSelectUser={(u) => selectUser('b', u)}
              onClear={() => clearSelection('b')}
              onBasketChange={(v) => setSideB((p) => ({ ...p, basketId: v }))}
            />
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={runCompare} disabled={!canCompare}>
              {comparing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <GitCompareArrows className="h-4 w-4 mr-2" />}
              Compare
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && <DiffView result={result} />}
    </StandardLayout>
  );
};

export default SuperAdminBasketComparePage;
